import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, CircleAlert, CircleCheck, Copy, ExternalLink, MapPinPlus } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/notify';
import { SearchField } from '@/components/ui/search-field';
import { SidePanel } from '@/components/ui/side-panel';
import { SystemChip } from '@/components/ui/system-chip';
import {
  formatPin,
  locationPin,
  parsePinInput,
  pinOf,
  projectLabel,
  sameSitePinSource,
  wazeLinkFor,
} from './mapModel';
import { cleanText, describeLocation, plural, projectCity, safeExternalUrl, siteKeyOf } from './projectsModel';

/*
  Projects map › "Add missing pins" (03 #10): every project of the current view without a map pin, one field
  each for a Waze link or coordinates (24.4539, 54.3773), checked as you type with the server's rules. Quick
  fills: the pin of another system of the same site, coordinates typed into the Location field. One save =
  ONE history entry (POST /projects/map-pins), so the toast's Undo puts every link back.
*/

function PinRow({ project, value, onChange, suggestions, inputRef }) {
  const parsed = parsePinInput(value);
  const oldLink = safeExternalUrl(project.waze_url);
  const place = projectCity(project) || describeLocation(project.location)?.text || '';
  const fieldId = `pin-input-${project.id}`;
  const statusId = `pin-status-${project.id}`;
  return (
    <li className="flex flex-col gap-1.5 border-b border-line-2 py-3 last:border-b-0" data-testid={`add-pin-row-${project.id}`}>
      <div className="flex min-w-0 items-center gap-2">
        <SystemChip systemType={project.system_type} size="sm" />
        <label htmlFor={fieldId} className="min-w-0 truncate text-[13.5px] font-semibold text-ink">{cleanText(project.name) || 'Untitled project'}</label>
        {place ? <span className="min-w-0 truncate text-[12px] text-ink-3">{place}</span> : null}
      </div>
      {oldLink ? (
        <p className="text-[12px] text-ink-3">
          Its Waze link has no pin —{' '}
          <a href={oldLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-brand-ink hover:underline">
            open it in Waze<ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
          , tap Share › Copy link and paste it here.
        </p>
      ) : null}
      <Input
        ref={inputRef}
        id={fieldId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Waze link or 24.4539, 54.3773"
        autoComplete="off"
        spellCheck={false}
        aria-invalid={parsed && !parsed.ok ? true : undefined}
        aria-describedby={parsed ? statusId : undefined}
        className={cn('h-9', parsed && !parsed.ok && 'border-status-red')}
        data-testid={`add-pin-input-${project.id}`}
      />
      {parsed ? (
        <p
          id={statusId}
          className={cn('inline-flex items-start gap-1 text-[12px] font-semibold', parsed.ok ? 'text-status-green-ink' : 'text-status-red-ink')}
          data-testid={`add-pin-status-${project.id}`}
        >
          {parsed.ok ? <CircleCheck className="mt-px h-[13px] w-[13px] shrink-0" aria-hidden="true" /> : <CircleAlert className="mt-px h-[13px] w-[13px] shrink-0" aria-hidden="true" />}
          {parsed.ok ? `Pin found · ${formatPin(parsed.pin)}${parsed.kind === 'waze' ? '' : ' · saved as a Waze link'}` : parsed.message}
        </p>
      ) : null}
      {parsed?.swapped ? (
        <div>
          <button
            type="button"
            onClick={() => onChange(`${parsed.swapped.lat}, ${parsed.swapped.lng}`)}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-dashed border-[color:var(--line-dashed)] px-2.5 py-0.5 text-[12px] font-semibold text-brand-ink hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`add-pin-swap-${project.id}`}
          >
            <ArrowLeftRight className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">Swap to {parsed.swapped.lat}, {parsed.swapped.lng}</span>
          </button>
        </div>
      ) : null}
      {!value && suggestions.length ? (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.key}
              type="button"
              onClick={() => onChange(suggestion.value)}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-dashed border-[color:var(--line-dashed)] px-2.5 py-0.5 text-[12px] font-semibold text-brand-ink hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`add-pin-suggestion-${suggestion.key}-${project.id}`}
            >
              <Copy className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{suggestion.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/**
 * projects: the projects without a pin (the map view's list); allProjects: every loaded project (same-site
 * pins); focusIds: start with these (a row's "Add pin"); onSaved(): reload the list.
 */
export function AddPinsPanel({ open, onOpenChange, projects, allProjects, focusIds = [], onSaved }) {
  const [values, setValues] = useState({});
  const [query, setQuery] = useState('');
  const inputs = useRef({});
  const focusKey = focusIds.join('|');

  // A row's "Add pin" puts its projects first, and the cursor in the first field.
  const rows = useMemo(() => {
    const first = new Set(focusIds);
    return [...projects].sort((a, b) => {
      const af = first.has(a.id) ? 0 : 1;
      const bf = first.has(b.id) ? 0 : 1;
      return af - bf || projectLabel(a).localeCompare(projectLabel(b), undefined, { sensitivity: 'base' });
    });
  }, [projects, focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !focusIds.length) return undefined;
    const timer = window.setTimeout(() => inputs.current[focusIds[0]]?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open, focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const words = cleanText(query).toLowerCase().split(/\s+/).filter(Boolean);
  const shown = words.length
    ? rows.filter((project) => {
      const text = [project.name, project.system_type, project.client, projectCity(project), project.location].map((v) => cleanText(v).toLowerCase()).join(' ');
      return words.every((word) => text.includes(word));
    })
    : rows;

  const filled = rows.filter((project) => cleanText(values[project.id]));
  const invalid = filled.filter((project) => !parsePinInput(values[project.id])?.ok);

  const suggestionsFor = (project) => {
    const list = [];
    const sibling = sameSitePinSource(project, allProjects);
    if (sibling) {
      const pin = pinOf(sibling);
      list.push({ key: 'site', label: `Use the pin of ${projectLabel(sibling)}`, value: safeExternalUrl(sibling.waze_url) || wazeLinkFor(pin) });
    } else {
      // A pin typed above for another system of the same site.
      const typed = rows.find((other) => other.id !== project.id && siteKeyOf(other) === siteKeyOf(project)
        && parsePinInput(values[other.id])?.ok);
      if (typed) list.push({ key: 'site', label: `Same pin as ${projectLabel(typed)}`, value: cleanText(values[typed.id]) });
    }
    const fromLocation = locationPin(project);
    if (fromLocation) list.push({ key: 'location', label: `Use the coordinates in Location (${formatPin(fromLocation)})`, value: `${fromLocation.lat}, ${fromLocation.lng}` });
    return list;
  };

  const save = async () => {
    if (!filled.length) throw new Error('Paste a Waze link or coordinates for at least one project');
    if (invalid.length) {
      throw new Error(`${plural(invalid.length, 'field')} ${invalid.length === 1 ? 'has' : 'have'} no usable pin (${projectLabel(invalid[0])}) — fix or clear ${invalid.length === 1 ? 'it' : 'them'}`);
    }
    const response = await api.post('/projects/map-pins', {
      pins: filled.map((project) => ({ id: project.id, link: cleanText(values[project.id]) })),
    });
    const updated = Number(response?.data?.updated) || 0;
    if (updated) notify.success(`${plural(updated, 'project')} put on the map`);
    else notify.info('These pins were already saved — nothing changed');
    onSaved?.();
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      icon={MapPinPlus}
      title="Add missing pins"
      subtitle={`${plural(projects.length, 'project')} in this view ${projects.length === 1 ? 'has' : 'have'} no map pin. Paste a Waze link or coordinates — Navigate in Waze then works from the map.`}
      dirty={filled.length}
      onSave={save}
      saveLabel={filled.length ? `Save ${plural(filled.length, 'pin')}` : 'Save pins'}
      saveDisabled={!filled.length}
      testId="add-pins-panel"
    >
      {rows.length > 6 ? (
        <SearchField
          value={query}
          onChange={setQuery}
          label="Find a project"
          placeholder="Find a project…"
          shortcut={false}
          className="mb-1 shrink-0"
          testId="add-pins-search"
          // Enter in a panel field saves; here it only filters.
          onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
        />
      ) : null}
      {shown.length ? (
        <ul className="flex flex-col">
          {shown.map((project) => (
            <PinRow
              key={project.id}
              project={project}
              value={values[project.id] || ''}
              onChange={(value) => setValues((previous) => ({ ...previous, [project.id]: value }))}
              suggestions={suggestionsFor(project)}
              inputRef={(element) => { inputs.current[project.id] = element; }}
            />
          ))}
        </ul>
      ) : (
        <p className="py-6 text-center text-[13px] text-ink-3">
          {rows.length ? `No project without a pin matches “${query}”.` : 'Every project in this view is on the map.'}
        </p>
      )}
    </SidePanel>
  );
}

export default AddPinsPanel;
